import { useState, useCallback, useRef } from "react";
import * as THREE from "three";
import { useDicomStore } from "../redux/dicomRedux";

// ----------- Helper for DICOM-correct quad geometry -------------
function createDicomSlicePlaneFromCorners(corners, texture) {
  // corners: array of 4 [x, y, z] (top-left, top-right, bottom-left, bottom-right)
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array([
    ...corners[0],
    ...corners[1],
    ...corners[2],
    ...corners[3],
  ]);
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  // UVs: (0,0)=TL, (1,0)=TR, (0,1)=BL, (1,1)=BR
  const uvs = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]);
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex([0, 2, 1, 2, 3, 1]);

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 1,
    depthWrite: false,
  });

  return new THREE.Mesh(geometry, material);
}

// Simple colored quad for helpers (no texture)
function createDicomHelperPlaneFromCorners(corners) {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array([
    ...corners[0],
    ...corners[1],
    ...corners[2],
    ...corners[3],
  ]);
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const uvs = new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]);
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex([0, 2, 1, 2, 3, 1]);

  const material = new THREE.MeshBasicMaterial({
    color: 0xffe066,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  return new THREE.Mesh(geometry, material);
}

const useSectionView = (modelRef, sceneRef) => {
  // DICOM geometry info
  const dicomBounds = useDicomStore((s) => s.dicomBounds);
  const dicomOrientation = useDicomStore((s) => s.dicomOrientation);
  const volume = useDicomStore((s) => s.volume);

  const [isSectionViewMode, setIsSectionViewMode] = useState(false);
  const [sectionViewMode, setSectionViewMode] = useState("none");
  const [planeOffset, setPlaneOffset] = useState(0);
  const [showHelper, setShowHelper] = useState(true);

  // Which half to hide (false = original, true = inverted)
  const [invertClip, setInvertClip] = useState(false);

  const planeHelperRef = useRef(null);

  // ---- Computes 4-corner slice quad for the plane (DICOM-physical, not axis-aligned) ----
  const getSliceCorners = useCallback(
    (sectionViewMode, index) => {
      if (
        !dicomBounds?.corners ||
        !dicomOrientation?.row ||
        !dicomOrientation?.col
      )
        return null;

      const rows = volume?.rows || 1;
      const cols = volume?.cols || 1;
      const depth = volume?.depth || 1;

      if (sectionViewMode === "axial") {
        // Interpolate between the 4 corners of the bottom-most and top-most slices
        const t = Math.min(Math.max(index, 0), depth - 1) / (depth - 1);
        const k0 = dicomBounds.corners.slice(0, 4);
        const k1 = dicomBounds.corners.slice(4, 8);
        return [
          new THREE.Vector3()
            .fromArray(k0[0])
            .lerp(new THREE.Vector3().fromArray(k1[0]), t)
            .toArray(),
          new THREE.Vector3()
            .fromArray(k0[1])
            .lerp(new THREE.Vector3().fromArray(k1[1]), t)
            .toArray(),
          new THREE.Vector3()
            .fromArray(k0[2])
            .lerp(new THREE.Vector3().fromArray(k1[2]), t)
            .toArray(),
          new THREE.Vector3()
            .fromArray(k0[3])
            .lerp(new THREE.Vector3().fromArray(k1[3]), t)
            .toArray(),
        ];
      }

      if (sectionViewMode === "coronal") {
        // CORRECT: vary along rows (Y) -> faces [row=0] vs [row=max]: [0,1,4,5] ↔ [2,3,6,7]
        const t = Math.min(Math.max(index, 0), rows - 1) / (rows - 1);
        const k0 = [0, 1, 4, 5].map((i) => dicomBounds.corners[i]);
        const k1 = [2, 3, 6, 7].map((i) => dicomBounds.corners[i]);
        return [
          new THREE.Vector3()
            .fromArray(k0[0])
            .lerp(new THREE.Vector3().fromArray(k1[0]), t)
            .toArray(),
          new THREE.Vector3()
            .fromArray(k0[1])
            .lerp(new THREE.Vector3().fromArray(k1[1]), t)
            .toArray(),
          new THREE.Vector3()
            .fromArray(k0[2])
            .lerp(new THREE.Vector3().fromArray(k1[2]), t)
            .toArray(),
          new THREE.Vector3()
            .fromArray(k0[3])
            .lerp(new THREE.Vector3().fromArray(k1[3]), t)
            .toArray(),
        ];
      }

      if (sectionViewMode === "sagittal") {
        // Vary along columns (X). Faces: left [0,2,4,6] ↔ right [1,3,5,7]
        // Order corners as TL, TR, BL, BR
        const t = Math.min(Math.max(index, 0), cols - 1) / (cols - 1);
        const kL = [0, 2, 4, 6].map((i) => dicomBounds.corners[i]); // left face (col=0)
        const kR = [1, 3, 5, 7].map((i) => dicomBounds.corners[i]); // right face (col=max)

        return [
          new THREE.Vector3()
            .fromArray(kL[0])
            .lerp(new THREE.Vector3().fromArray(kR[0]), t)
            .toArray(), // TL
          new THREE.Vector3()
            .fromArray(kL[2])
            .lerp(new THREE.Vector3().fromArray(kR[2]), t)
            .toArray(), // TR
          new THREE.Vector3()
            .fromArray(kL[1])
            .lerp(new THREE.Vector3().fromArray(kR[1]), t)
            .toArray(), // BL
          new THREE.Vector3()
            .fromArray(kL[3])
            .lerp(new THREE.Vector3().fromArray(kR[3]), t)
            .toArray(), // BR
        ];
      }

      return null;
    },
    [dicomBounds, dicomOrientation, volume]
  );

  // ----------- Apply section view (with optional invert override) -------------
  const applySectionView = useCallback(
    (opts = {}) => {
      if (!modelRef.current || !sceneRef.current) return;

      const invertOverride = Object.prototype.hasOwnProperty.call(
        opts,
        "invertClip"
      )
        ? opts.invertClip
        : null;

      // Dispose old helper (texture/material/geometry) and remove
      if (planeHelperRef.current) {
        const old = planeHelperRef.current;
        if (old.material?.map) old.material.map.dispose();
        old.material?.dispose?.();
        old.geometry?.dispose?.();
        sceneRef.current.remove(old);
        planeHelperRef.current = null;
      }

      let count = 1;
      if (sectionViewMode === "axial") count = volume?.depth || 1;
      if (sectionViewMode === "coronal") count = volume?.rows || 1;
      if (sectionViewMode === "sagittal") count = volume?.cols || 1;

      const idx = Math.min(Math.max(Math.round(planeOffset), 0), count - 1);
      const corners = getSliceCorners(sectionViewMode, idx);

      // Build clipping plane from three points (TL, TR, BL)
      let clippingPlane = null;
      if (corners) {
        const p0 = new THREE.Vector3(...corners[0]);
        const p1 = new THREE.Vector3(...corners[1]);
        const p2 = new THREE.Vector3(...corners[2]);
        const normal = new THREE.Vector3()
          .crossVectors(p1.clone().sub(p0), p2.clone().sub(p0))
          .normalize();
        clippingPlane = new THREE.Plane(normal, -normal.dot(p0));
      }

      // Plane side toggle
      const effectiveInvert =
        invertOverride !== null ? invertOverride : invertClip;
      const planeForModel =
        clippingPlane && effectiveInvert
          ? clippingPlane.clone().negate()
          : clippingPlane;

      // Apply the clipping plane to the model
      modelRef.current.children.forEach((child) => {
        if (child.material) {
          const applyToMat = (mat) => {
            mat.clippingPlanes = planeForModel ? [planeForModel] : [];
            mat.needsUpdate = true;
          };
          if (Array.isArray(child.material)) child.material.forEach(applyToMat);
          else applyToMat(child.material);
        }
      });

      // --- DICOM VOLUME TEXTURE SECTION VIEW ---
      if (volume && volume.data && corners && clippingPlane && showHelper) {
        const { data, rows, cols, depth } = volume;

        let sliceArr, w, h;
        if (sectionViewMode === "axial") {
          sliceArr = data[idx];
          w = cols;
          h = rows;
        } else if (sectionViewMode === "coronal") {
          w = cols;
          h = depth;
          sliceArr = new Int16Array(w * h);
          for (let z = 0; z < depth; z++) {
            for (let x = 0; x < cols; x++) {
              sliceArr[z * w + x] = data[z][idx * cols + x];
            }
          }
        } else if (sectionViewMode === "sagittal") {
          // Horizontal axis = depth (Z), vertical axis = rows (Y)
          w = depth;
          h = rows;
          sliceArr = new Int16Array(w * h);
          for (let y = 0; y < rows; y++) {
            for (let z = 0; z < depth; z++) {
              sliceArr[y * w + z] = data[z][y * cols + idx];
            }
          }
        }

        // normalize to 0-255 (simple)
        const pixels = new Uint8Array(w * h);
        let mn = sliceArr[0],
          mx = sliceArr[0];
        for (let i = 1; i < sliceArr.length; i++) {
          if (sliceArr[i] < mn) mn = sliceArr[i];
          if (sliceArr[i] > mx) mx = sliceArr[i];
        }
        const range = mx - mn || 1;
        for (let i = 0; i < pixels.length; i++) {
          pixels[i] = Math.round(((sliceArr[i] - mn) / range) * 255);
        }

        // create RGBA buffer
        const rgba = new Uint8Array(w * h * 4);
        for (let i = 0; i < w * h; i++) {
          const v = pixels[i];
          rgba[i * 4 + 0] = v;
          rgba[i * 4 + 1] = v;
          rgba[i * 4 + 2] = v;
          rgba[i * 4 + 3] = 255;
        }
        const tex = new THREE.DataTexture(rgba, w, h, THREE.RGBAFormat);
        tex.needsUpdate = true;

        const helper = createDicomSlicePlaneFromCorners(corners, tex);
        sceneRef.current.add(helper);
        planeHelperRef.current = helper;
        return;
      }

      // --- GEOMETRIC HELPER (NO DICOM VOLUME) ---
      if (corners && showHelper) {
        const helper = createDicomHelperPlaneFromCorners(corners);
        sceneRef.current.add(helper);
        planeHelperRef.current = helper;
      }
    },
    [
      modelRef,
      sceneRef,
      sectionViewMode,
      planeOffset,
      showHelper,
      volume,
      invertClip,
      getSliceCorners,
    ]
  );

  return {
    isSectionViewMode,
    sectionViewMode,
    setIsSectionViewMode,
    setSectionViewMode,
    applySectionView,
    planeOffset,
    setPlaneOffset,
    showHelper,
    setShowHelper,
    setInvertClip,
  };
};

export default useSectionView;
